import { authorName, formatNoteDate } from '@/lib/note-format';
import type { MatrixSummary } from '@mss_creator/shared';
import { Icon } from '@pms-digital/design-system';
import { Link } from '@tanstack/react-router';
import { type FocusEvent, useId, useState } from 'react';

export interface MatrixNotesCounterProps {
  matrix: Pick<MatrixSummary, 'id' | 'openNoteCount' | 'memoPreview' | 'lastNote'>;
  /** The clock of the relative date; tests pin it. */
  now?: Date;
}

/** The matrix page opens on its Notes view (`?vue=notes`). */
const NOTES_VIEW_SEARCH = { vue: 'notes' } as const;

/**
 * The note counter of a matrix card (issue #19, planche 13): a gray bubble and
 * the number of open discussions the user can see, beside the « ⋯ » menu —
 * nothing at zero. A click leads to the Notes view of the matrix. Hovering or
 * focusing it shows the memo (three lines at most) and the latest note, with
 * « Voir les N notes ».
 */
export function MatrixNotesCounter({ matrix, now }: MatrixNotesCounterProps) {
  const [open, setOpen] = useState(false);
  const tooltipId = useId();
  if (matrix.openNoteCount === 0) return null;

  const lastNoteAuthor = matrix.lastNote?.authorName
    ? { displayName: matrix.lastNote.authorName }
    : null;
  const label =
    matrix.openNoteCount === 1 ? '1 note ouverte' : `${matrix.openNoteCount} notes ouvertes`;

  function handleBlur(event: FocusEvent<HTMLSpanElement>) {
    const next = event.relatedTarget;
    if (next instanceof Node && event.currentTarget.contains(next)) return;
    setOpen(false);
  }

  return (
    <span
      className="relative inline-flex"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      onFocus={() => setOpen(true)}
      onBlur={handleBlur}
      onKeyDown={(event) => event.key === 'Escape' && setOpen(false)}
    >
      <Link
        to="/matrices/$matrixId"
        params={{ matrixId: matrix.id }}
        search={NOTES_VIEW_SEARCH}
        aria-label={`${label}, voir les notes`}
        aria-describedby={tooltipId}
        className="inline-flex h-7 items-center gap-1 rounded-sm px-1 text-caption text-gray-600 no-underline hover:bg-gray-100 hover:text-noir focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-bleu-nuit"
      >
        <Icon name="chat_bubble" size={14} />
        {matrix.openNoteCount}
      </Link>
      <span hidden={!open} className="absolute top-full right-0 z-20 pt-1">
        <span
          id={tooltipId}
          role="tooltip"
          className="flex w-72 flex-col gap-2 rounded-md border border-gray-200 bg-white p-2 text-left text-badge text-noir shadow-lg"
        >
          {matrix.memoPreview && (
            <span className="flex flex-col gap-0">
              <span className="font-semibold text-gray-500 text-micro uppercase tracking-wide">
                Mémo
              </span>
              <span className="line-clamp-3 whitespace-pre-line break-words">
                {matrix.memoPreview}
              </span>
            </span>
          )}
          {matrix.lastNote && (
            <span className="flex flex-col gap-0">
              <span className="font-semibold text-gray-500 text-micro uppercase tracking-wide">
                Dernière note
              </span>
              <span>
                <b className="font-semibold">{authorName(lastNoteAuthor)}</b>{' '}
                <span className="text-gray-500">
                  {formatNoteDate(matrix.lastNote.createdAt, now)}
                </span>
              </span>
              <span className="line-clamp-3 break-words">{matrix.lastNote.excerpt}</span>
            </span>
          )}
          <Link
            to="/matrices/$matrixId"
            params={{ matrixId: matrix.id }}
            search={NOTES_VIEW_SEARCH}
            className="self-start text-bleu-nuit underline underline-offset-2"
          >
            {matrix.openNoteCount === 1 ? 'Voir la note' : `Voir les ${matrix.openNoteCount} notes`}
          </Link>
        </span>
      </span>
    </span>
  );
}
